import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthUser } from '@/lib/auth';
import { lastWorkspace, readOrgClaim, rememberWorkspace } from '@/lib/workspace';

/**
 * Route "/" to the user's workspace, onboarding, or login.
 *
 * The installed app opens here every time, so this is the first thing a
 * phone does on every launch. It used to run a collection-group query and
 * an org read before it would navigate anywhere, which put two round trips
 * in front of the home screen on every open, and it imported Firestore,
 * which put the whole database SDK in front of the sign-in page.
 *
 * Now: signed out is decided from Auth alone. Signed in, the workspace this
 * device last opened is used straight away when the token still lists it.
 * Only a device that has never opened a workspace loads Firestore, and then
 * the token's claim names the workspaces before any query has to.
 */
export function RootRedirect() {
  const { user, loading } = useAuthUser();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  // A step below that fails (the Firestore chunk gone after a deploy, or a
  // read with no signal and nothing cached) is thrown at render, so the
  // route's error screen says so and offers a reload. Unhandled, it left
  // "Opening your workspace." on screen for good.
  const [failure, setFailure] = useState<unknown>(null);

  useEffect(() => {
    if (loading || checked) return;
    if (!user) {
      navigate('/login');
      return;
    }
    setChecked(true);
    // Signed in, so the next screen is inside the shell: start fetching it
    // now, alongside the claim, rather than after the navigation.
    void import('./AppShell').catch(() => undefined);
    void (async () => {
      try {
        const claim = await readOrgClaim(user);
        const last = lastWorkspace();
        if (last && claim[last.orgId]) {
          navigate(`/o/${last.slug}`);
          return;
        }
        const [{ db }, { collectionGroup, doc, getDoc, getDocs, limit, query, where }] = await Promise.all([
          import('@/lib/firebase'),
          import('firebase/firestore'),
        ]);
        // The claim lists every workspace this account belongs to. It can lag
        // a membership made seconds ago, so an empty one falls back to the
        // members query this redirect always ran.
        let orgId: string | undefined = Object.keys(claim)[0];
        if (!orgId) {
          const memberships = await getDocs(
            query(collectionGroup(db, 'members'), where('uid', '==', user.uid), limit(1)),
          );
          orgId = memberships.empty ? undefined : memberships.docs[0]!.ref.parent.parent!.id;
        }
        if (!orgId) {
          navigate('/onboarding');
          return;
        }
        const orgSnap = await getDoc(doc(db, `orgs/${orgId}`));
        const slug = orgSnap.get('slug') as string | undefined;
        if (slug) rememberWorkspace(slug, orgId, true);
        navigate(slug ? `/o/${slug}` : '/onboarding');
      } catch (err) {
        setFailure(err instanceof Error ? err : new Error(String(err)));
      }
    })();
  }, [user, loading, checked, navigate]);

  if (failure) throw failure;
  return <p className="p-8 text-sm text-ink-3">Opening your workspace.</p>;
}
